// The record's By product ledger and its sections per product (pipeline RC4, stage 3; brief of 7 Oct section 4 items 7
// and 8, drawing a6-account-file.html "By product", "OneVideo" and "OneTrade"). The ledger's rows are ledger.ts's; a
// product's section opens with its reading from the one company read, the chip and the sentence the verdict row gives,
// and the measures under it are the Usage panel's, which follows the sections.
import type { ReactNode } from 'react';
import type { CompanyRead, ProductReading } from './company-read';
import { ledgerOf, type ServiceByProduct } from './ledger';
import { OneTradeCharts } from './OneTradeCharts';
import { InFrontPanels, OneVideoAgreements, OneVideoLeagues } from './OneVideoPanels';
import { wentDirectWords } from './InFrontNote';
import { lastCompleteMonth, type CompanyReadings } from './readings';

const jump = (id: string) => (e: React.MouseEvent) => {
  const el = document.getElementById(id);
  if (el) { e.preventDefault(); el.scrollIntoView({ block: 'start', behavior: 'smooth' }); }
};

function Cell({ label, main, note, chip }: { label: string; main: string; note: string | null; chip?: boolean }) {
  return (
    <td data-l={label}>
      {chip ? <span className="lg-chip">{main}</span> : <span className="lg-main">{main}</span>}
      {note ? <span className="lg-note">{note}</span> : null}
    </td>
  );
}

export function ProductLedger({ read, service }: { read: CompanyRead; service: ServiceByProduct[] | null }) {
  const l = ledgerOf(read, service);
  return (
    <section className="sec" id="a-products">
      <div className="sh"><h2>By product</h2><span className="sl">{l.sub}</span></div>
      <div className="sb">
        {l.rows.length === 0
          ? <p className="none">No product is on record for {read.company.name}: no money, no agreement and no deal with a figure.</p>
          : (
            <div className="lg-wrap">
              <table className="lg">
                <thead><tr><th>Product</th><th className="n">Forecast, 12 months</th><th>Potential, from deals</th><th>Agreements</th><th>Use, against its own pattern</th><th>Service</th></tr></thead>
                <tbody>
                  {l.rows.map((r) => (
                    <tr key={r.product}>
                      <th scope="row">{r.anchor ? <a className="lg-prod" href={`#${r.anchor}`} onClick={jump(r.anchor)}>{r.product}</a> : <span className="lg-prod">{r.product}</span>}</th>
                      <td className="n" data-l="Forecast, 12 months"><span className="lg-fig">{r.forecast.main}</span>{r.forecast.note ? <span className="lg-note">{r.forecast.note}</span> : null}</td>
                      <Cell label="Potential, from deals" {...r.potential} />
                      <Cell label="Agreements" {...r.agreements} />
                      <Cell label="Use" main={r.use.main} note={r.use.note} chip={r.use.chip} />
                      <Cell label="Service" {...r.service} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        {l.notTaken.length ? <p className="lg-nottaken">Not taken: {l.notTaken.map((p, i) => <span key={p}>{i ? ', ' : ''}<b>{p}</b></span>)}.</p> : null}
      </div>
    </section>
  );
}

const SECTIONS: Array<{ id: string; product: string; key: keyof CompanyRead['readings'] }> = [
  { id: 'a-onevideo', product: 'OneVideo', key: 'oneVideo' },
  { id: 'a-onetrade', product: 'OneTrade', key: 'oneTrade' },
  { id: 'a-infront', product: "InFront's streams", key: 'inFront' },
];

/** One section per product whose use has been read, or read and found nothing to read; a reading not built draws none. */
export function ProductSections({ read, readings = null, name, inFrontNote = null, seats = null }: { read: CompanyRead; readings?: CompanyReadings | null; name: string; inFrontNote?: ReactNode; seats?: ReactNode }) {
  const shown = SECTIONS.map((s) => ({ ...s, r: read.readings[s.key] as ProductReading })).filter((s) => s.r.state !== 'not_built');
  if (shown.length === 0) return null;
  // Each section's drawing under its sentence, once the readings have come (fetched after the head draws).
  const drawn = (id: string): ReactNode => (!readings ? null
    // REQ-2026-10-07-167: the seats sentence, moved here from the Usage section Mike called defunct.
    : id === 'a-onetrade' ? <><OneTradeCharts readings={readings} />{seats}</>
    : id === 'a-onevideo' ? <><OneVideoAgreements agreements={readings.oneVideo} month={lastCompleteMonth(read.asOf)} /><OneVideoLeagues lists={readings.oneVideoLeagues ?? []} /></>
    : id === 'a-infront' ? <><InFrontPanels readings={readings.inFront} name={name} wentDirect={read.inFrontRoute ? wentDirectWords(read.inFrontRoute) : null} />{inFrontNote}</>
    : null);
  return <>{shown.map((s) => <ProductSection key={s.id} id={s.id} product={s.product} r={s.r}>{drawn(s.id)}</ProductSection>)}</>;
}

function ProductSection({ id, product, r, children }: { id: string; product: string; r: ProductReading; children?: ReactNode }): ReactNode {
  return (
    <section className="sec" id={id}>
      <div className="sh">
        <h2>{product}</h2>
        {r.state === 'read' ? <span className="lg-chip">{r.chip}</span> : r.state === 'none' ? <span className="sl">No reading yet</span> : null}
      </div>
      <div className="sb">
        <p className="lg-head">{r.state === 'read' ? r.sentence : r.state === 'none' ? r.words : null}</p>
        {children}
      </div>
    </section>
  );
}
